<template>
  <div class="tab1" v-if="!loading">
    <!-- Week navigator -->
    <div class="week-navigator mb-4">
      <button
        type="button"
        class="week-nav-button"
        :disabled="!canGoPrev"
        aria-label="Tuần trước"
        @click="goPrevWeek"
      >
        ‹
      </button>
      <div class="week-nav-content">
        <div class="week-nav-title">{{ weekLabel }}</div>
        <div v-if="weekData" class="week-nav-range">{{ weekDateRange }}</div>
      </div>
      <button
        type="button"
        class="week-nav-button"
        :disabled="!canGoNext"
        aria-label="Tuần sau"
        @click="goNextWeek"
      >
        ›
      </button>
    </div>

    <!-- Section: Thông tin khám -->
    <b-card class="mb-4">
      <h6 slot="header" class="mb-0">Thông tin khám</h6>
      <b-row>
        <b-col md="6">
          <BasicDatePicker v-model="examForm.examinationDate" label="Ngày khám" name="examinationDate" format="YYYY-MM-DD" value-type="format" />
          <SourceValueNotice :notice="pendingUpdates.examinationDate" :can-apply="isUpdatePermission" @apply="applySourceValue('examinationDate')" />
        </b-col>
        <b-col md="6">
          <BasicInput v-model="examForm.doctorName" label="Tên Bác sĩ" name="doctorName" placeholder="Nhập tên Bác sĩ" />
          <SourceValueNotice :notice="pendingUpdates.doctorName" :can-apply="isUpdatePermission" @apply="applySourceValue('doctorName')" />
        </b-col>
        <b-col md="12">
          <BasicTextArea v-model="examForm.medication" label="Thuốc" name="medication" placeholder="Nhập thuốc" />
          <SourceValueNotice :notice="pendingUpdates.medication" :can-apply="isUpdatePermission" @apply="applySourceValue('medication')" />
        </b-col>
        <b-col md="12">
          <BasicTextArea v-model="examForm.treatmentNote" label="Ghi chú điều trị" name="treatmentNote" placeholder="Nhập ghi chú" />
          <SourceValueNotice :notice="pendingUpdates.treatmentNote" :can-apply="isUpdatePermission" @apply="applySourceValue('treatmentNote')" />
        </b-col>
      </b-row>
      <div class="text-right mt-3">
        <LoadingButton v-if="isUpdatePermission" class="btn btn-success" :handler="saveExamination">Lưu thông tin khám</LoadingButton>
      </div>
    </b-card>

    <!-- Section: Theo dõi chỉ số -->
    <b-card class="metrics-card mb-4">
      <div slot="header" class="metrics-title">
        <i class="fas fa-wave-square"></i>
        <span>Theo dõi chỉ số</span>
      </div>
      <div class="expected-weight-box mb-4">
        <div>
          <div class="expected-weight-label">Cân nặng dự kiến</div>
          <div class="expected-weight-value">{{ expectedWeightDisplay }}</div>
        </div>
        <i class="fas fa-chart-line expected-weight-icon"></i>
      </div>
      <div class="metrics-grid">
        <div>
          <BasicInput v-model="trackingForm.weight" label="Cân nặng thực tế (kg)" name="weight" type="number" @input="calcBmi('tracking')" />
          <SourceValueNotice :notice="pendingUpdates.weight" :can-apply="isUpdatePermission" @apply="applySourceValue('weight')" />
        </div>
        <BasicInput v-model="trackingForm.bmi" label="BMI (Kg/m²)" name="bmi" type="number" disabled />
        <div>
          <BasicInput v-model="trackingForm.waist" label="Vòng eo (cm)" name="waist" type="number" />
          <SourceValueNotice :notice="pendingUpdates.waist" :can-apply="isUpdatePermission" @apply="applySourceValue('waist')" />
        </div>
        <BasicInput v-model="trackingForm.hip" label="Vòng mông (cm)" name="hip" type="number" />
        <BasicInput v-model="trackingForm.muscleMass" label="Khối lượng Cơ (kg)" name="muscleMass" type="number" />
        <BasicInput v-model="trackingForm.fatMass" label="Khối lượng Mỡ (kg)" name="fatMass" type="number" />
        <BasicInput v-model="trackingForm.bodyFatPercentage" label="Tỷ lệ Mỡ (%)" name="bodyFatPercentage" type="number" />
        <BasicInput v-model="trackingForm.visceralFatLevel" label="Mỡ nội tạng (Level)" name="visceralFatLevel" type="number" />
        <BasicInput v-model="trackingForm.waterMass" label="Khối lượng Nước (kg)" name="waterMass" type="number" />
        <div>
          <BasicInput v-model="trackingForm.systolicBloodPressure" label="Huyết áp tâm thu (mmHg)" name="systolicBloodPressure" type="number" />
          <SourceValueNotice :notice="pendingUpdates.systolicBloodPressure" :can-apply="isUpdatePermission" @apply="applySourceValue('systolicBloodPressure')" />
        </div>
        <div>
          <BasicInput v-model="trackingForm.diastolicBloodPressure" label="Huyết áp tâm trương (mmHg)" name="diastolicBloodPressure" type="number" />
          <SourceValueNotice :notice="pendingUpdates.diastolicBloodPressure" :can-apply="isUpdatePermission" @apply="applySourceValue('diastolicBloodPressure')" />
        </div>
        <div>
          <BasicInput v-model="trackingForm.heartRate" label="Mạch / Nhịp tim (lần/phút)" name="heartRate" type="number" />
          <SourceValueNotice :notice="pendingUpdates.heartRate" :can-apply="isUpdatePermission" @apply="applySourceValue('heartRate')" />
        </div>
        <div>
          <BasicInput v-model="trackingForm.fastingBloodSugar" label="Đường huyết đói (mmol/L)" name="fbs" type="number" />
          <SourceValueNotice :notice="pendingUpdates.fastingBloodSugar" :can-apply="isUpdatePermission" @apply="applySourceValue('fastingBloodSugar')" />
        </div>
        <div>
          <BasicInput v-model="trackingForm.postprandialBloodSugar" label="Đường huyết sau ăn (mmol/L)" name="ppbs" type="number" />
          <SourceValueNotice :notice="pendingUpdates.postprandialBloodSugar" :can-apply="isUpdatePermission" @apply="applySourceValue('postprandialBloodSugar')" />
        </div>
        <div>
          <BasicInput v-model="trackingForm.hba1c" label="HbA1c (%)" name="hba1c" type="number" />
          <SourceValueNotice :notice="pendingUpdates.hba1c" :can-apply="isUpdatePermission" @apply="applySourceValue('hba1c')" />
        </div>
      </div>
      <BasicTextArea v-model="trackingForm.additionalNotes" label="Chỉ số bổ sung & ghi chú" name="additionalNotes" />
      <div class="text-right mt-3">
        <LoadingButton v-if="isUpdatePermission" class="btn btn-success" :handler="saveTracking">Lưu chỉ số</LoadingButton>
      </div>
    </b-card>

    <!-- Section: Kế hoạch hành động -->
    <b-card class="mb-4">
      <h6 slot="header" class="mb-0">Kế hoạch hành động thay đổi lối sống</h6>
      <div v-for="(action, idx) in weeklyActionsForm" :key="action.id || action.actionPlanId || action._uid" class="action-card mb-3 p-3 border rounded">
        <b-row>
          <b-col md="10" class="mb-2">
            <strong>{{ action.activityType }} — {{ action.action }}</strong>
            <span class="text-muted ml-2">{{ action.frequency }} {{ action.unit }}</span>
            <span v-if="action.note" class="text-muted ml-2">({{ action.note }})</span>
            <span v-if="action.id && !action.dirty" class="badge badge-success ml-2">Đã lưu</span>
            <span v-else class="badge badge-warning ml-2">Chưa lưu</span>
          </b-col>
          <b-col md="2" class="mb-2 text-right">
            <LoadingButton v-if="isUpdatePermission" class="btn btn-sm btn-success mr-1" :handler="() => saveOneWeeklyAction(idx)">
              <i class="fas fa-save"></i>
            </LoadingButton>
            <button v-if="isDeletePermission" type="button" class="btn btn-sm btn-outline-danger" @click="removeWeeklyAction(idx)">
              <i class="fas fa-trash"></i>
            </button>
          </b-col>

          <b-col md="8"><BasicInput v-model="action.action" label="Hành động" :name="'action-'+idx" @input="() => markDirty(idx)" /></b-col>
          <b-col md="4"><BasicInput v-model="action.frequency" label="Tần suất kế hoạch" :name="'frequency-'+idx" type="number" :input-number="true" @input="() => markDirty(idx)" /></b-col>
          <b-col md="4"><BasicInput v-model="action.unit" label="Đơn vị" :name="'unit-'+idx" @input="() => markDirty(idx)" /></b-col>
          <b-col md="4"><BasicInput v-model="action.note" label="Ghi chú kế hoạch" :name="'note-'+idx" @input="() => markDirty(idx)" /></b-col>
          <b-col md="4"><BasicInput v-model="action.actualFrequency" label="Tần suất thực tế" :name="'actualFreq-'+idx" :input-number="true" @input="() => markDirty(idx)" /></b-col>
          <b-col md="4">
            <BasicSelect
              v-model="action.status"
              label="Tình trạng"
              :name="'status-'+idx"
              :options="statusOptions"
              track-by="id"
              value-label="name"
              :change-value-by-object="true"
              @input="() => markDirty(idx)"
            />
          </b-col>
          <b-col md="4"><BasicInput v-model="action.nextWeekPlanFrequency" label="KH tần suất tuần tới" :name="'nwpf-'+idx" type="number" :input-number="true" @input="() => markDirty(idx)" /></b-col>
          <b-col md="6"><BasicTextArea v-model="action.weeklyNote" label="Ghi chú tuần này" :name="'wn-'+idx" @input="() => markDirty(idx)" /></b-col>
          <b-col md="6"><BasicTextArea v-model="action.nextWeekGoal" label="Mục tiêu tuần tới" :name="'nwg-'+idx" @input="() => markDirty(idx)" /></b-col>
          <b-col md="6">
            <BasicSelect
              v-model="action.overrideScope"
              label="Phạm vi áp dụng"
              :name="'scope-'+idx"
              :options="scopeOptions"
              track-by="id"
              value-label="name"
              :change-value-by-object="true"
              @input="() => markDirty(idx)"
            />
          </b-col>
        </b-row>
      </div>
      <div v-if="weeklyActionsForm.length === 0" class="text-muted text-center py-3">
        Chưa có kế hoạch hành động. Vui lòng thiết lập ở Tab "Tổng trạng &amp; Mục tiêu".
      </div>
      <div class="mt-3">
        <button v-if="isInsertPermission" type="button" class="btn btn-light-success" @click="addCustomAction">
          Thêm hành động khác
        </button>
      </div>
    </b-card>

  </div>
  <div v-else class="text-center py-6">
    <b-spinner />
  </div>
</template>

<script>
import {
  getWeekDetail,
  getBaseline,
  saveExamination,
  saveTracking,
  saveWeeklyActions,
  deleteWeeklyAction,
  getKpiInputsByRange,
} from '../services/services';
import {
  hisItemTime,
  latestVisit,
  prescriptionsOfVisit,
  medicationsOfVisit,
  formatMedication,
  doctorAndNoteOf,
  maxHisItemTime,
  latestHba1c,
} from '../services/his-data';
import { getLatestCheckupByPhoneInRange } from '@/core/services/his.api.service';
import SourceValueNotice from './SourceValueNotice.vue';

// Field có nguồn HIS/App → hiện "Có dữ liệu mới" khi nguồn mới hơn lần coach lưu
const EXAM_SOURCE_FIELDS = ['examinationDate', 'doctorName', 'medication', 'treatmentNote'];
const TRACKING_SOURCE_FIELDS = [
  'weight', 'waist', 'systolicBloodPressure', 'diastolicBloodPressure', 'heartRate',
  'fastingBloodSugar', 'postprandialBloodSugar', 'hba1c',
];

export default {
  name: 'Tab1WeekCurrent',
  components: { SourceValueNotice },
  props: {
    userPackageId: { type: String, default: null },
    weekNumber: { type: Number, default: 0 },
    weekList: {
      type: Array,
      default: () => [],
    },
    // weekList đã tải xong (kể cả rỗng) → mới load tuần
    weekListReady: { type: Boolean, default: false },
    externalId: { type: String, default: '' },
    patientId: { type: Number, default: null },
    isViewPermission: { type: Boolean, default: false },
    isInsertPermission: { type: Boolean, default: false },
    isUpdatePermission: { type: Boolean, default: false },
    isDeletePermission: { type: Boolean, default: false },
  },
  data() {
    return {
      // Chờ weekList xong mới load tuần → hiện spinner thay vì form trống
      loading: true,
      weekData: null,
      hisCheckupData: null,
      // Bỏ qua response cũ khi đổi tuần/hồ sơ nhanh
      loadSeq: 0,
      // Thời điểm coach lưu từng phần (unix); null = chưa lưu
      savedAt: { exam: null, tracking: null },
      // field → { value, source: 'HIS' | 'App', at }
      sourceValues: {},
      expectedWeight: null,
      examForm: {
        examinationDate: null,
        doctorName: '',
        medication: '',
        treatmentNote: '',
      },
      trackingForm: {
        height: null,
        weight: null,
        bmi: null,
        waist: null,
        hip: null,
        muscleMass: null,
        fatMass: null,
        bodyFatPercentage: null,
        visceralFatLevel: null,
        waterMass: null,
        hba1c: null,
        fastingBloodSugar: null,
        postprandialBloodSugar: null,
        systolicBloodPressure: null,
        diastolicBloodPressure: null,
        heartRate: null,
        bloodPressure: '',
        additionalNotes: '',
      },
      weeklyActionsForm: [],
      uidCounter: 0,
      statusOptions: [
        { id: 0, name: 'Hoàn thành tốt' },
        { id: 1, name: 'Cần cố gắng' },
        { id: 2, name: 'Đang cập nhật' },
      ],
      scopeOptions: [
        { id: 0, name: 'Không' },
        { id: 1, name: 'Chỉ tuần tới' },
        { id: 2, name: 'Toàn bộ lộ trình còn lại' },
      ],
    };
  },
  computed: {
    weekLabel() {
      return 'Tuần ' + this.weekNumber;
    },
    weekDateRange() {
      if (!this.weekData) return '';
      const start = this.$moment.unix(this.weekData.weekStartDate).utcOffset('+07:00').format('DD/MM/YYYY');
      const end = this.$moment.unix(this.weekData.weekEndDate).utcOffset('+07:00').format('DD/MM/YYYY');
      return `${start} - ${end}`;
    },
    availableWeeks() {
      return this.weekList
        .map((week) => week.weekNumber)
        .filter((weekNumber) => weekNumber > 0)
        .sort((a, b) => a - b);
    },
    minWeek() {
      return this.availableWeeks.length ? this.availableWeeks[0] : this.weekNumber;
    },
    maxWeek() {
      return this.availableWeeks.length ? this.availableWeeks[this.availableWeeks.length - 1] : this.weekNumber;
    },
    currentWeekIndex() {
      return this.availableWeeks.indexOf(this.weekNumber);
    },
    canGoPrev() {
      return this.currentWeekIndex > 0 || (!this.availableWeeks.length && this.weekNumber > this.minWeek);
    },
    canGoNext() {
      return (this.currentWeekIndex >= 0 && this.currentWeekIndex < this.availableWeeks.length - 1)
        || (!this.availableWeeks.length && this.weekNumber < this.maxWeek);
    },
    expectedWeightDisplay() {
      return this.expectedWeight !== null && this.expectedWeight !== undefined && this.expectedWeight !== ''
        ? `${this.expectedWeight} kg`
        : '-- kg';
    },
    weekMeta() {
      return this.weekList.find((w) => w.weekNumber === this.weekNumber) || null;
    },
    // Chỉ load lại khi tuần / khoảng ngày / hồ sơ HIS đổi; weekList reload sau khi lưu
    // (chỉ đổi isCompleted) không làm mất dữ liệu đang nhập
    loadKey() {
      if (!this.weekListReady) return null;
      return [
        this.userPackageId,
        this.weekNumber,
        this.weekMeta?.weekStartDate,
        this.weekMeta?.weekEndDate,
        this.externalId,
        this.patientId,
      ].join('|');
    },
    pendingUpdates() {
      const result = {};
      [...EXAM_SOURCE_FIELDS, ...TRACKING_SOURCE_FIELDS].forEach((field) => {
        const src = this.sourceValues[field];
        const savedAt = this.savedAt[EXAM_SOURCE_FIELDS.includes(field) ? 'exam' : 'tracking'];
        if (!src || !savedAt || !src.at || src.at <= savedAt) return;
        if (this.isSameValue(this.formOf(field)[field], src.value)) return;
        const at = this.$moment.unix(src.at).utcOffset('+07:00').format('HH:mm DD/MM');
        result[field] = {
          ...src,
          text: `Có dữ liệu mới: ${this.displaySourceValue(field, src.value)} (${src.source}, ${at})`,
        };
      });
      return result;
    },
  },
  watch: {
    loadKey: {
      immediate: true,
      handler(val) {
        if (val) this.loadWeekData();
      },
    },
  },
  methods: {
    normalizeStatus(value) {
      if (value && typeof value === 'object') return value;
      if (typeof value === 'number') {
        return this.statusOptions.find(option => option.id === value) || null;
      }
      if (value == null || value === '') return null;
      const map = {
        'Hoàn thành tốt': 0,
        'Cần cố gắng': 1,
        'Đang cập nhật': 2,
      };
      return this.statusOptions.find(option => option.id === map[value]) || null;
    },
    normalizeOverrideScope(value) {
      if (value && typeof value === 'object') return value;
      if (typeof value === 'number') {
        return this.scopeOptions.find(option => option.id === value) || this.scopeOptions[0];
      }
      if (value == null || value === '') return this.scopeOptions[0];
      const map = {
        'Không': 0,
        'Chỉ tuần tới': 1,
        'Toàn bộ lộ trình còn lại': 2,
      };
      return this.scopeOptions.find(option => option.id === map[value]) || this.scopeOptions[0];
    },
    mapWeeklyAction(a) {
      const realId = (a.id && a.id !== '00000000-0000-0000-0000-000000000000') ? a.id : null;
      // Hành động chưa lưu → để trống các field chỉ có khi đã save
      const isSaved = !!realId;
      return {
        id: realId,
        weeklyActionId: realId,
        actionPlanId: a.actionPlanId,
        action: a.action || '',
        frequency: a.frequency ?? null,
        unit: a.unit || '',
        note: a.note || '',
        activityType: a.activityType || '',
        actualFrequency: isSaved ? (a.actualFrequency || '') : '',
        status: isSaved ? this.normalizeStatus(a.status) : null,
        weeklyNote: isSaved ? (a.weeklyNote || '') : '',
        nextWeekGoal: isSaved ? (a.nextWeekGoal || '') : '',
        nextWeekPlanFrequency: isSaved ? (a.nextWeekPlanFrequency || '') : '',
        overrideScope: isSaved ? this.normalizeOverrideScope(a.overrideScope) : null,
        isCustom: !a.actionPlanId,
        dirty: false,
        _uid: this.nextUid(),
      };
    },
    nextUid() {
      return ++this.uidCounter;
    },
    markDirty(idx) {
      this.weeklyActionsForm[idx].dirty = true;
    },
    addCustomAction() {
      this.weeklyActionsForm.push({
        id: null,
        weeklyActionId: null,
        actionPlanId: null,
        activityType: '',
        action: '',
        frequency: null,
        unit: '',
        note: '',
        actualFrequency: '',
        status: null,
        weeklyNote: '',
        nextWeekGoal: '',
        nextWeekPlanFrequency: '',
        overrideScope: null,
        isCustom: true,
        dirty: true,
        _uid: this.nextUid(),
      });
    },
    async removeWeeklyAction(index) {
      const a = this.weeklyActionsForm[index];
      const confirmed = await this.$swal({
        title: 'Xác nhận xóa',
        text: `Bạn có chắc muốn xóa hành động "${a.action || a.activityType}"?`,
        icon: 'warning',
        buttons: {
          cancel: { text: 'Huỷ', value: false, visible: true, closeModal: true },
          confirm: { text: 'Xóa', value: true, visible: true, closeModal: true },
        },
      });
      if (!confirmed) return;
      const actionId = a.id || a.weeklyActionId;
      if (actionId) {
        await deleteWeeklyAction(actionId);
      }
      this.weeklyActionsForm.splice(index, 1);
      if (actionId) await this.afterSave();
    },
    convertDateToTimestamp(dateStr, format) {
      return this.$moment(dateStr, format).utcOffset('+07:00', true).unix();
    },
    parseBloodPressure(value) {
      const match = String(value || '').match(/(\d+)\s*\/\s*(\d+)/);
      return {
        systolic: match ? Number(match[1]) : null,
        diastolic: match ? Number(match[2]) : null,
      };
    },
    formatBloodPressure(systolic, diastolic) {
      const hasSystolic = systolic !== null && systolic !== undefined && systolic !== '';
      const hasDiastolic = diastolic !== null && diastolic !== undefined && diastolic !== '';
      return hasSystolic && hasDiastolic ? `${systolic}/${diastolic}` : '';
    },
    goPrevWeek() {
      if (this.currentWeekIndex > 0) {
        this.$emit('change-week', this.availableWeeks[this.currentWeekIndex - 1]);
      } else if (this.canGoPrev) {
        this.$emit('change-week', this.weekNumber - 1);
      }
    },
    goNextWeek() {
      if (this.currentWeekIndex >= 0 && this.currentWeekIndex < this.availableWeeks.length - 1) {
        this.$emit('change-week', this.availableWeeks[this.currentWeekIndex + 1]);
      } else if (this.canGoNext) {
        this.$emit('change-week', this.weekNumber + 1);
      }
    },
    emptyTrackingForm() {
      return {
        height: null, weight: null, bmi: null, waist: null, hip: null,
        muscleMass: null, fatMass: null, bodyFatPercentage: null, visceralFatLevel: null,
        waterMass: null, hba1c: null, fastingBloodSugar: null, postprandialBloodSugar: null,
        systolicBloodPressure: null, diastolicBloodPressure: null, heartRate: null,
        bloodPressure: '', additionalNotes: '',
      };
    },
    async loadWeekData() {
      if (!this.userPackageId) {
        this.loading = false;
        return;
      }
      const seq = ++this.loadSeq;
      this.loading = true;
      // Reset toàn bộ form trước khi load tuần mới
      this.weekData = null;
      this.hisCheckupData = null;
      this.savedAt = { exam: null, tracking: null };
      this.sourceValues = {};
      this.examForm = { examinationDate: null, doctorName: '', medication: '', treatmentNote: '' };
      this.trackingForm = this.emptyTrackingForm();
      this.expectedWeight = null;
      this.weeklyActionsForm = [];
      try {
        const weekMeta = this.weekMeta;
        const hasRange = !!(weekMeta?.weekStartDate && weekMeta?.weekEndDate);
        const [res, hisRes, baselineRes, kpiRes] = await Promise.all([
          getWeekDetail(this.userPackageId, this.weekNumber),
          (this.externalId || this.patientId) && hasRange
            ? getLatestCheckupByPhoneInRange(
                this.externalId,
                weekMeta.weekStartDate,
                weekMeta.weekEndDate,
                this.patientId,
              ).catch(() => null)
            : Promise.resolve(null),
          getBaseline(this.userPackageId).catch(() => null),
          hasRange
            ? getKpiInputsByRange(this.userPackageId, weekMeta.weekStartDate, weekMeta.weekEndDate).catch(() => null)
            : Promise.resolve(null),
        ]);
        // Đã chuyển tuần / hồ sơ khác trong lúc chờ → bỏ response cũ
        if (seq !== this.loadSeq) return;
        const data = res?.data ?? res;
        this.weekData = data;
        // HIS API response body: { data: {...}, meta: {...} } → unwrap .data
        this.hisCheckupData = hisRes?.data ?? hisRes;
        const kpi = kpiRes?.data ?? kpiRes;

        // Set weeklyActions trước, độc lập với HIS
        this.weeklyActionsForm = (data.weeklyActions || []).map(this.mapWeeklyAction);

        const bd = baselineRes?.data ?? baselineRes;
        // Tính expectedWeight từ baseline — chạy độc lập, không phụ thuộc vào HIS
        if (this.weekNumber >= 1 && this.weekNumber <= 8) {
          const baselineWeight = bd?.baseline?.weight ?? null;
          const goalWeight = bd?.goalNote?.weightGoal ?? null;
          if (baselineWeight !== null && goalWeight !== null) {
            this.expectedWeight = Math.round(
              (baselineWeight - ((baselineWeight - goalWeight) / 8) * this.weekNumber) * 100,
            ) / 100;
          }
        }

        // Luôn hiện giá trị coach đã lưu; HIS/App chỉ điền sẵn cho phần chưa lưu
        this.fillFromCoachNote(data);
        this.savedAt = {
          exam: data.examination?.savedAt || null,
          tracking: data.trackingIndicator?.savedAt || null,
        };
        this.sourceValues = this.buildSourceValues(this.hisCheckupData, kpi);
        if (!this.savedAt.exam) this.prefillFromSource(EXAM_SOURCE_FIELDS);
        if (!this.savedAt.tracking) {
          this.prefillFromSource([...TRACKING_SOURCE_FIELDS, 'height', 'bmi']);
          this.prefillAdditionalNotes(kpi);
        }
        // Chiều cao không nhập theo tuần → lấy từ tổng trạng ban đầu để tính BMI
        if (!this.trackingForm.height && bd?.baseline?.height) {
          this.trackingForm.height = Math.round(bd.baseline.height * 100);
        }
        if (!this.trackingForm.bmi) this.calcBmi('tracking');
      } catch (e) {
        if (seq === this.loadSeq) this.$toastr.e({ title: 'Lỗi', msg: 'Không thể tải dữ liệu tuần.' });
      } finally {
        if (seq === this.loadSeq) this.loading = false;
      }
    },
    fillFromCoachNote(data) {
      const ex = data.examination || {};
      this.examForm = {
        examinationDate: ex.examinationDate
          ? this.$moment.unix(ex.examinationDate).utcOffset('+07:00').format('YYYY-MM-DD')
          : null,
        doctorName: ex.doctorName || '',
        medication: ex.medication || '',
        treatmentNote: ex.treatmentNote || '',
      };
      const tr = data.trackingIndicator || {};
      const parsedBloodPressure = this.parseBloodPressure(tr.bloodPressure);
      this.trackingForm = {
        height: tr.height ? Math.round(tr.height * 100) : null,
        weight: tr.weight ?? null,
        bmi: tr.bmi ?? null,
        waist: tr.waist ?? null,
        hip: tr.hip ?? null,
        muscleMass: tr.muscleMass ?? null,
        fatMass: tr.fatMass ?? null,
        bodyFatPercentage: tr.bodyFatPercentage ?? null,
        visceralFatLevel: tr.visceralFatLevel ?? null,
        waterMass: tr.waterMass ?? null,
        hba1c: tr.hba1c ?? null,
        fastingBloodSugar: tr.fastingBloodSugar ?? null,
        postprandialBloodSugar: tr.postprandialBloodSugar ?? null,
        systolicBloodPressure: tr.systolicBloodPressure ?? parsedBloodPressure.systolic,
        diastolicBloodPressure: tr.diastolicBloodPressure ?? parsedBloodPressure.diastolic,
        heartRate: tr.heartRate ?? null,
        bloodPressure: tr.bloodPressure || '',
        additionalNotes: tr.additionalNotes || '',
      };
      // Chỉ dùng tr.expectedWeight nếu chưa có giá trị đã tính từ baseline
      this.expectedWeight = this.expectedWeight ?? tr.expectedWeight ?? null;
    },
    // Giá trị nguồn cho từng field: HIS ưu tiên, App (kpi-inputs-range) dự phòng
    buildSourceValues(his, kpi) {
      const src = {};
      const put = (field, value, source, at) => {
        if (src[field] || value === null || value === undefined || value === '' || value === 0) return;
        src[field] = { value, source, at: at || null };
      };

      // HIS — sinh hiệu của lần khám mới nhất trong tuần
      const vital = his?.vitalSigns?.[0];
      if (vital) {
        const at = hisItemTime(vital);
        put('weight', vital.weight, 'HIS', at);
        put('height', vital.height, 'HIS', at);
        put('bmi', vital.bmi, 'HIS', at);
        put('waist', vital.waistCircumference, 'HIS', at);
        put('systolicBloodPressure', vital.bloodPressureSystolic, 'HIS', at);
        put('diastolicBloodPressure', vital.bloodPressureDiastolic, 'HIS', at);
        put('heartRate', vital.heartRate, 'HIS', at);
      }
      const hba1c = latestHba1c(his);
      if (hba1c) put('hba1c', hba1c.value, 'HIS', hba1c.at);

      // HIS — thông tin khám theo lần khám mới nhất + đơn thuốc / thuốc của đúng lần khám đó
      const visit = latestVisit(his);
      if (visit?.admissionDate) {
        put('examinationDate', this.$moment(visit.admissionDate).format('YYYY-MM-DD'), 'HIS', hisItemTime(visit));
      }
      const { doctor, doctorAt, note, noteAt } = doctorAndNoteOf(prescriptionsOfVisit(his, visit));
      put('doctorName', doctor, 'HIS', doctorAt);
      put('treatmentNote', note, 'HIS', noteAt);
      const meds = medicationsOfVisit(his, visit);
      if (meds.length) put('medication', meds.map(formatMedication).join('\n'), 'HIS', maxHisItemTime(meds));

      // App
      const w = kpi?.weight, bp = kpi?.bloodPressure, bs = kpi?.bloodSugar, h = kpi?.hbA1c;
      put('weight', w?.weight, 'App', w?.date);
      put('height', w?.height, 'App', w?.date);
      put('bmi', w?.bmi, 'App', w?.date);
      put('waist', w?.waist, 'App', w?.date);
      put('systolicBloodPressure', bp?.systolic, 'App', bp?.date);
      put('diastolicBloodPressure', bp?.diastolic, 'App', bp?.date);
      put('heartRate', bp?.pulseRate, 'App', bp?.date);
      put('fastingBloodSugar', bs?.fastingGlucose, 'App', bs?.fastingDate);
      put('postprandialBloodSugar', bs?.postprandialGlucose, 'App', bs?.postprandialDate);
      put('hba1c', h?.hbA1c, 'App', h?.date);
      return src;
    },
    formOf(field) {
      return EXAM_SOURCE_FIELDS.includes(field) ? this.examForm : this.trackingForm;
    },
    isEmptyValue(value) {
      return value === null || value === undefined || String(value).trim() === '';
    },
    isSameValue(a, b) {
      if (this.isEmptyValue(a) || this.isEmptyValue(b)) return this.isEmptyValue(a) && this.isEmptyValue(b);
      const na = Number(a), nb = Number(b);
      if (!isNaN(na) && !isNaN(nb)) return Math.abs(na - nb) < 1e-6;
      const norm = (v) => String(v).replace(/\r\n/g, '\n').trim();
      return norm(a) === norm(b);
    },
    displaySourceValue(field, value) {
      if (field === 'examinationDate') return this.$moment(value, 'YYYY-MM-DD').format('DD/MM/YYYY');
      return value;
    },
    // Phần chưa lưu: điền sẵn các field còn trống từ nguồn
    prefillFromSource(fields) {
      fields.forEach((field) => {
        const src = this.sourceValues[field];
        const form = this.formOf(field);
        if (src && this.isEmptyValue(form[field])) form[field] = src.value;
      });
    },
    prefillAdditionalNotes(kpi) {
      if (this.trackingForm.additionalNotes) return;
      // Chỉ số bổ sung & ghi chú — các field HIS không có input riêng
      const vital = this.hisCheckupData?.vitalSigns?.[0] || {};
      const extras = [];
      if (vital.temperature) extras.push(`Nhiệt độ: ${vital.temperature} (°C)`);
      if (vital.respiratoryRate) extras.push(`Nhịp thở: ${vital.respiratoryRate} (lần/phút)`);
      if (vital.oxygenSaturation) extras.push(`SpO2: ${vital.oxygenSaturation} (%)`);
      if (vital.glucoseLevel) extras.push(`Đường huyết: ${vital.glucoseLevel} (mmol/L)`);
      if (extras.length) {
        this.trackingForm.additionalNotes = extras.join('\n');
        return;
      }
      const ex = kpi?.exercise;
      if (ex && (ex.duration || ex.burnedCalorie)) {
        this.trackingForm.additionalNotes = `Vận động: ${ex.duration || 0} phút, ${ex.burnedCalorie || 0} kcal`;
      }
    },
    // "Dùng giá trị này" → chép vào input, coach tự bấm lưu
    applySourceValue(field) {
      const src = this.sourceValues[field];
      if (!src) return;
      this.formOf(field)[field] = src.value;
      if (field === 'weight') this.calcBmi('tracking');
    },
    afterSave() {
      // Chỉ cập nhật trạng thái tuần (ProgressTracker), KHÔNG reload lại trang
      // để tránh mất các mục đang nhập dở.
      this.$emit('week-completed');
    },
    async saveExamination() {
      const payload = {
        coachNoteId: this.weekData?.id,
        examinationDate: this.examForm.examinationDate
          ? this.convertDateToTimestamp(this.examForm.examinationDate, 'YYYY-MM-DD')
          : null,
        doctorName: this.examForm.doctorName,
        medication: this.examForm.medication,
        treatmentNote: this.examForm.treatmentNote,
      };
      await saveExamination(payload);
      // Đánh dấu đã lưu → ẩn "Có dữ liệu mới" của các nguồn cũ hơn
      this.savedAt = { ...this.savedAt, exam: this.$moment().unix() };
      this.$toastr.s({ title: 'Thành công', msg: 'Đã lưu thông tin khám.' });
      await this.afterSave();
    },
    calcBmi(form) {
      const f = form === 'tracking' ? this.trackingForm : null;
      if (!f) return;
      const h = parseFloat(f.height);
      const w = parseFloat(f.weight);
      if (h > 0 && w > 0) {
        const hm = h / 100;
        f.bmi = Math.round((w / (hm * hm)) * 10) / 10;
      }
    },
    async saveTracking() {
      const payload = {
        coachNoteId: this.weekData?.id,
        ...this.trackingForm,
        height: this.trackingForm.height ? this.trackingForm.height / 100 : null,
        bloodPressure: this.formatBloodPressure(
          this.trackingForm.systolicBloodPressure,
          this.trackingForm.diastolicBloodPressure,
        ),
      };
      await saveTracking(payload);
      this.savedAt = { ...this.savedAt, tracking: this.$moment().unix() };
      this.$toastr.s({ title: 'Thành công', msg: 'Đã lưu chỉ số theo dõi.' });
      await this.afterSave();
    },
    async saveOneWeeklyAction(idx) {
      const a = this.weeklyActionsForm[idx];
      if (!a.actionPlanId && !(a.action && a.action.trim())) return;
      const confirmed = await this.$swal({
        title: 'Xác nhận lưu',
        text: `Bạn có chắc muốn lưu hành động "${a.action || a.activityType}"?`,
        icon: 'warning',
        buttons: {
          cancel: { text: 'Huỷ', value: false, visible: true, closeModal: true },
          confirm: { text: 'Lưu', value: true, visible: true, closeModal: true },
        },
      });
      if (!confirmed) return;
      const payload = {
        coachNoteId: this.weekData?.id,
        actions: [{
          id: a.id || a.weeklyActionId,
          actionPlanId: a.actionPlanId,
          activityType: a.activityType,
          action: a.action,
          frequency: a.frequency,
          unit: a.unit,
          note: a.note,
          actualFrequency: a.actualFrequency,
          status: a.status && typeof a.status === 'object' ? a.status.id : a.status,
          weeklyNote: a.weeklyNote,
          nextWeekGoal: a.nextWeekGoal,
          nextWeekPlanFrequency: a.nextWeekPlanFrequency,
          overrideScope: a.overrideScope && typeof a.overrideScope === 'object' ? a.overrideScope.id : a.overrideScope,
        }],
      };
      const res = await saveWeeklyActions(payload);
      const saved = (res?.data ?? res ?? [])[0];
      const savedId = saved?.id && saved.id !== '00000000-0000-0000-0000-000000000000'
        ? saved.id
        : (a.id || a.weeklyActionId);
      // Cập nhật cục bộ item vừa lưu, không reload (giữ các hành động khác đang nhập dở)
      this.$set(this.weeklyActionsForm, idx, {
        ...this.weeklyActionsForm[idx],
        id: savedId,
        weeklyActionId: savedId,
        dirty: false,
      });
      this.$toastr.s({ title: 'Thành công', msg: 'Đã lưu hành động.' });
      this.afterSave();
    },
    async saveWeeklyActions() {
      const payload = {
        coachNoteId: this.weekData?.id,
        actions: this.weeklyActionsForm
          .filter(a => a.actionPlanId || (a.action && a.action.trim()))
          .map(a => ({
          id: a.id || a.weeklyActionId,
          actionPlanId: a.actionPlanId,
          activityType: a.activityType,
          action: a.action,
          frequency: a.frequency,
          unit: a.unit,
          note: a.note,
          actualFrequency: a.actualFrequency,
          status: a.status && typeof a.status === 'object' ? a.status.id : a.status,
          weeklyNote: a.weeklyNote,
          nextWeekGoal: a.nextWeekGoal,
          nextWeekPlanFrequency: a.nextWeekPlanFrequency,
          overrideScope: a.overrideScope && typeof a.overrideScope === 'object' ? a.overrideScope.id : a.overrideScope,
        })),
      };
      await saveWeeklyActions(payload);
      this.$toastr.s({ title: 'Thành công', msg: 'Đã lưu kế hoạch hành động.' });
      await this.afterSave();
    },
  },
};
</script>

<style scoped>
.week-navigator {
  min-height: 80px;
  border: 1px solid #EBEDF3;
  border-radius: 10px;
  padding: 14px 22px;
  display: grid;
  grid-template-columns: 36px 1fr 36px;
  align-items: center;
  column-gap: 12px;
  background: #fff;
  box-shadow: 0 0 30px 0 rgba(82, 63, 105, 0.05);
}

.week-nav-button {
  width: 36px;
  height: 36px;
  border: 1px solid #EBEDF3;
  border-radius: 50%;
  background: #F3F6F9;
  color: #3F4254;
  font-size: 28px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.week-nav-button:hover:not(:disabled) {
  background: #E1F0FF;
  border-color: #3699FF;
  color: #3699FF;
}

.week-nav-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.week-nav-content {
  min-width: 0;
  text-align: center;
}

.week-nav-title {
  color: #181C32;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
}

.week-nav-range {
  margin-top: 4px;
  color: #B5B5C3;
  font-size: 13px;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.metrics-card {
  border: 1px solid #EBEDF3;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 0 30px 0 rgba(82, 63, 105, 0.05);
}

.metrics-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #181C32;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
}

.metrics-title i {
  color: #3699FF;
}

.expected-weight-box {
  min-height: 72px;
  border: 1px solid #E1F0FF;
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #F1F9FF;
}

.expected-weight-label {
  color: #7E8299;
  font-size: 13px;
  line-height: 1.4;
}

.expected-weight-value {
  margin-top: 4px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}

.expected-weight-icon {
  color: #3699FF;
  font-size: 22px;
  opacity: 0.5;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px 16px;
}

@media (max-width: 575.98px) {
  .week-navigator {
    padding: 12px;
    column-gap: 8px;
  }

  .week-nav-title {
    font-size: 18px;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }
}
</style>
